import React, { useEffect, useRef } from 'react';

// Every open Modal and ActionDialog, oldest first. The last one is on top.
// ponytail: ordered by when each opened; two dialogs mounted in the same commit with one
// nested inside the other would register child first. Nothing opens that way today.
const openDialogs: object[] = [];

/** True while any Modal / ActionDialog (so also a `useConfirm` question) is open. */
export const isDialogOpen = () => openDialogs.length > 0;

/**
 * How a dialog is dismissed without its own buttons: Escape, or a click on its backdrop.
 * One owner for Modal and ActionDialog, because they stack, and one gesture must close one
 * dialog, the one on top.
 *
 * VI-6 Chrome retest (2026-09-15): the invoice editor, the "Review vendor match" popup over
 * it and the in-app confirmation over both each listened for Escape on window, so one Escape
 * closed all of them (and a cell's own Escape closed the editor). The backdrops closed on
 * mousedown, so the release of that same press landed on whatever was underneath.
 *
 * - Escape reaches only the top dialog, and none when something inside already used it
 *   (EditableCell cancels its edit with preventDefault).
 * - The backdrop closes on a click that both started and ended on it.
 * - `disabled` (a busy dialog) swallows both without closing, and still keeps the Escape
 *   from falling through to the dialog underneath.
 */
export function useDialogDismiss(isOpen: boolean, onClose: () => void, disabled = false) {
  const latest = useRef({ onClose, disabled });
  latest.current = { onClose, disabled };
  const pressedOnBackdrop = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    const token = {};
    openDialogs.push(token);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (openDialogs[openDialogs.length - 1] !== token) return;
      e.preventDefault();
      if (!latest.current.disabled) latest.current.onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      openDialogs.splice(openDialogs.indexOf(token), 1);
    };
  }, [isOpen]);

  return {
    onMouseDown: (e: React.MouseEvent) => {
      pressedOnBackdrop.current = e.target === e.currentTarget;
    },
    onClick: (e: React.MouseEvent) => {
      const pressed = pressedOnBackdrop.current;
      pressedOnBackdrop.current = false;
      if (pressed && e.target === e.currentTarget && !latest.current.disabled) latest.current.onClose();
    },
  };
}
